import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, Select, Space, message, Switch, Tag, Modal, Form, Popconfirm, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall, makeDeleteCall } from '../../utils/helper';
import { useModalities } from '../../hooks/useModalities';
import './masters-config.css';

const { Option } = Select;

const ModalityGroups = () => {
  const [groupsList, setGroupsList] = useState({ data: [], loading: false, error: null });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [form] = Form.useForm();
  const { modalityOptions, loading: modalitiesLoading } = useModalities();

  const fetchGroups = useCallback(async () => {
    setGroupsList(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await makeGetCall('/modality-groups?all=true');
      if (response?.data?.success) {
        setGroupsList({ data: response.data.data || [], loading: false, error: null });
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch modality groups');
      }
    } catch (error) {
      setGroupsList({ data: [], loading: false, error: error.message || 'Failed to fetch modality groups' });
      message.error('Failed to fetch modality groups');
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, mg_modalities: [], mg_match_type: 'ALL' });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      mg_name: record.label,
      mg_modalities: record.modalities || [],
      mg_match_type: record.match_type || 'ALL',
      is_active: record.active,
    });
    setModalVisible(true);
  };

  const handleToggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      const res = await makePostCall('/modality-group', {
        mg_id: record.id,
        is_active: checked,
      });
      if (res?.data?.success) {
        message.success(`Group ${checked ? 'activated' : 'deactivated'}`);
        fetchGroups();
      } else {
        throw new Error(res?.data?.message || 'Failed to update group');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to update group');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id);
    try {
      const res = await makeDeleteCall(`/modality-group/${record.id}`);
      if (res?.data?.success) {
        message.success('Modality group deleted successfully');
        fetchGroups();
      } else {
        throw new Error(res?.data?.message || 'Failed to delete group');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete group');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...(editingRecord ? { mg_id: editingRecord.id } : {}),
        mg_name: values.mg_name?.trim(),
        mg_modalities: values.mg_modalities || [],
        mg_match_type: values.mg_match_type || 'ALL',
        is_active: values.is_active,
      };

      const res = await makePostCall('/modality-group', payload);
      if (res?.data?.success) {
        message.success(`Modality group ${editingRecord ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        fetchGroups();
      } else {
        throw new Error(res?.data?.message || 'Failed to save group');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || 'Failed to save group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const filteredGroups = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return (groupsList.data || []).filter(item => {
      if (statusFilter === 'active' && !item.active) return false;
      if (statusFilter === 'inactive' && item.active) return false;
      if (search) {
        const haystack = `${item.label} ${(item.modalities || []).join(' ')}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [groupsList.data, statusFilter, searchText]);

  const columns = [
    {
      title: 'Group Name',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => (a.label || '').localeCompare(b.label || ''),
    },
    {
      title: 'Modalities',
      dataIndex: 'modalities',
      key: 'modalities',
      render: (modalities) =>
        (modalities && modalities.length > 0)
          ? modalities.map((m) => <Tag key={m}>{m}</Tag>)
          : <span style={{ color: '#94a3b8' }}>—</span>,
    },
    {
      title: 'Reconcile',
      dataIndex: 'match_type',
      key: 'match_type',
      render: (matchType) =>
        (matchType === 'ANY')
          ? <Tag color="blue">Any one</Tag>
          : <Tag>All required</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={active}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit Group">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              Edit
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete this modality group?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />} loading={deletingId === record.id}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="masters-config-container">
      <div className="mc-titlebar">
        <span className="mc-title">Modality Groups</span>
        <Button type="primary" className="mc-add" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Group
        </Button>
      </div>

      <div className="mc-toolbar">
        <Space size="middle" wrap>
          <Input
            placeholder="Search by group name or modality"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {groupsList.error && (
        <div className="mc-error">
          <p>{groupsList.error}</p>
          <Button onClick={fetchGroups}>Retry</Button>
        </div>
      )}

      <div className="mc-panel">
        <div className="mc-panel-head">
          <span>Modality Groups</span>
          <span className="mc-count">{filteredGroups.length}</span>
        </div>
        <div className="mc-panel-body">
          <Table
            className="mc-table"
            columns={columns}
            dataSource={filteredGroups}
            loading={groupsList.loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} groups`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <Modal
        className="masters-config-modal"
        title={editingRecord ? 'Edit Modality Group' : 'Add Modality Group'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="admin-form">
          <Form.Item
            label="Group Name"
            name="mg_name"
            rules={[
              { required: true, message: 'Please enter the group name' },
              { max: 100, message: 'Name must be 100 characters or less' },
            ]}
          >
            <Input placeholder="e.g. Cross-sectional" />
          </Form.Item>

          <Form.Item
            label="Modalities"
            name="mg_modalities"
            tooltip="A group is a combination of modalities used later for HIS reconciliation (T002)."
          >
            <Select
              mode="multiple"
              placeholder="Select modalities"
              options={modalityOptions}
              loading={modalitiesLoading}
              allowClear
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            label="Reconciliation Mode"
            name="mg_match_type"
            tooltip="All required: every modality in the group must be received before the order reconciles (hybrid studies like PET-CT). Any one: receiving any single modality reconciles the order (x-ray, where DX and CR are interchangeable)."
          >
            <Select placeholder="Select reconciliation mode">
              <Option value="ALL">All required (every modality must arrive)</Option>
              <Option value="ANY">Any one (any single modality reconciles)</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item className="form-actions">
            <Button
              onClick={() => {
                setModalVisible(false);
                setEditingRecord(null);
                form.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModalityGroups;
